/**
 * Show archive — one checkbox, on every list.
 *
 * Archiving replaced deleting across the product, which raises the question
 * straight after: where did they go. A record that is out of the way still has
 * to be findable, or archiving is deleting with a kinder word on the button.
 *
 * This started as a three-way list — hide, show, only — and the client said
 * plainly that the dropdown was confusing. They were right: "archived only" is
 * a filter somebody reaches for about once a year, and it was sitting in
 * everybody's way for the one thing they actually want, which is to see the
 * archived rows alongside the live ones and put one back.
 *
 * So: a checkbox. Off is the list as it always was. On shows the archived rows
 * too, each marked and each offering the way back.
 */
export type ArchiveMode = 'hide' | 'show' | 'only';

export function ArchiveFilter({
  value,
  onChange,
  /** What the list holds, for the label: "Show archived people". */
  noun,
}: {
  value: ArchiveMode;
  onChange: (value: ArchiveMode) => void;
  noun?: string;
}) {
  const showing = value !== 'hide';

  return (
    <label className="flex shrink-0 cursor-pointer select-none items-center gap-2 text-sm text-body">
      <input
        type="checkbox"
        checked={showing}
        onChange={(event) => onChange(event.target.checked ? 'show' : 'hide')}
        className="h-4 w-4 rounded border-line text-navy focus:ring-navy"
      />
      Show archived{noun ? ` ${noun}` : ''}
    </label>
  );
}

/** What the query string carries. Absent when hidden, so a clean list has a clean URL. */
export const archiveParam = (mode: ArchiveMode) => (mode === 'hide' ? undefined : mode);
